/**
 * Time zone labels for an opening's dates, in the format the recruiter-ui hiring goal
 * modal uses: the abbreviation next to the dates, the long name in a tooltip. Names
 * come from `Intl` in English, so "PDT" reads the same for every viewer.
 */
const LOCALE = 'en-US';

type TimeZoneNameStyle = 'short' | 'long' | 'longGeneric' | 'longOffset';

// Building a formatter is the costly step, and a range check names many days in one zone.
const formatters = new Map<string, Intl.DateTimeFormat | null>();

function formatterFor(
  timeZone: string,
  style: TimeZoneNameStyle,
): Intl.DateTimeFormat | null {
  const key = `${timeZone}|${style}`;
  if (!formatters.has(key)) {
    try {
      formatters.set(
        key,
        new Intl.DateTimeFormat(LOCALE, { timeZone, timeZoneName: style }),
      );
    } catch {
      formatters.set(key, null);
    }
  }
  return formatters.get(key) ?? null;
}

function timeZoneName(
  date: Date,
  timeZone: string,
  style: TimeZoneNameStyle,
): string {
  return (
    formatterFor(timeZone, style)
      ?.formatToParts(date)
      .find(part => part.type === 'timeZoneName')?.value ?? ''
  );
}

/**
 * The zone's abbreviation on a date: "PDT" in September, "PST" in December. A zone
 * Intl names in words ("Hawaii-Aleutian Standard Time") becomes its initials; one
 * with no abbreviation keeps its offset ("GMT+9").
 * @param date - Any instant on the day to name. eg new Date(2026, 8, 15, 12)
 * @param timeZone - IANA zone. eg 'America/Los_Angeles'
 * @returns The abbreviation, or '' for an unknown zone. eg 'PDT'
 */
export function getTimeZoneAbbr(date: Date, timeZone: string): string {
  const abbr = timeZoneName(date, timeZone, 'short');
  if (abbr.length > 4 && /[A-Za-z ]+ Time/.test(abbr)) {
    return abbr
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase();
  }
  return abbr;
}

/**
 * The zone's full name on a date, with daylight saving: "Pacific Daylight Time".
 * @param date - Any instant on the day to name. eg new Date(2026, 8, 15, 12)
 * @param timeZone - IANA zone. eg 'America/Los_Angeles'
 * @returns The long name, or '' for an unknown zone. eg 'Pacific Daylight Time'
 */
export function getTimeZoneLongName(date: Date, timeZone: string): string {
  return timeZoneName(date, timeZone, 'long');
}

const DAY_MS = 24 * 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

// The zone's offset from UTC at an instant, in minutes: -420 for PDT, 0 for UTC.
function offsetMinutes(date: Date, timeZone: string): number {
  const match = /GMT([+-])(\d{2}):(\d{2})/.exec(
    timeZoneName(date, timeZone, 'longOffset'),
  );
  if (!match) return 0;
  const minutes = Number(match[2]) * 60 + Number(match[3]);
  return match[1] === '-' ? -minutes : minutes;
}

// The instant a calendar day begins at the opening. The offsets in force before and
// after the day give the candidates; one is midnight unless a change skips it. When a
// change falls back at midnight both are, and the day begins at the earlier. When it
// skips midnight neither is, and the day begins where the later offset takes over,
// which the earlier offset's candidate names.
function localMidnight(day: Date, timeZone: string): Date {
  const utc = Date.UTC(day.getFullYear(), day.getMonth(), day.getDate());
  const atOffset = (instant: number) =>
    utc - offsetMinutes(new Date(instant), timeZone) * MINUTE_MS;
  const candidates = [
    atOffset(utc - 14 * HOUR_MS),
    atOffset(utc + 14 * HOUR_MS),
  ];
  const midnights = candidates.filter(
    candidate => atOffset(candidate) === candidate,
  );
  return new Date(midnights.length ? Math.min(...midnights) : candidates[0]);
}

/**
 * Whether the zone's name changes anywhere in the range: from the first day's
 * midnight at the opening, through noon of each day, to the last day's final
 * instant. A change can fall early on the first day (Nov 1 starts in PDT), between
 * two days with the same name (January to December), or only in a later year of a
 * long range (Cairo resumed daylight saving in 2023), so every day is checked.
 */
function crossesChange(
  from: Date,
  to: Date,
  timeZone: string,
  firstAbbr: string,
): boolean {
  const lastDay = new Date(to.getFullYear(), to.getMonth(), to.getDate() + 1);
  const end = new Date(localMidnight(lastDay, timeZone).getTime() - 1);
  if (getTimeZoneAbbr(end, timeZone) !== firstAbbr) return true;

  const firstNoon = Date.UTC(
    from.getFullYear(),
    from.getMonth(),
    from.getDate(),
    12,
  );
  for (let noon = firstNoon; noon <= end.getTime(); noon += DAY_MS) {
    if (getTimeZoneAbbr(new Date(noon), timeZone) !== firstAbbr) return true;
  }
  return false;
}

/**
 * The label for a range of calendar days at an opening. A range that crosses a
 * daylight-saving change names the zone without daylight saving ("Pacific Time"),
 * since either abbreviation is wrong for part of it.
 * @param from - The first day, as picked in the browser. eg new Date(2026, 8, 1)
 * @param to - The last day; the first day stands in when absent. eg new Date(2026, 8, 7)
 * @param timeZone - The opening's IANA zone. eg 'America/Los_Angeles'
 * @returns The visible label and the tooltip name, or null for an unknown zone. eg { short: 'PDT', long: 'Pacific Daylight Time (PDT)' }
 */
export function getRangeTimeZoneLabel(
  from: Date,
  to: Date | undefined,
  timeZone: string,
): { short: string; long: string } | null {
  const start = localMidnight(from, timeZone);
  const abbr = getTimeZoneAbbr(start, timeZone);
  if (!abbr) return null;

  if (crossesChange(from, to ?? from, timeZone, abbr)) {
    const generic =
      timeZoneName(start, timeZone, 'longGeneric') ||
      getTimeZoneLongName(start, timeZone);
    return { short: generic, long: generic };
  }

  const longName = getTimeZoneLongName(start, timeZone);
  return {
    short: abbr,
    long: longName && longName !== abbr ? `${longName} (${abbr})` : abbr,
  };
}
